import { useCallback, useEffect, useRef } from "react"
import { Platform } from "react-native"

import { useAppState } from "@react-native-community/hooks"

import {
  clearContextToken,
  getContextToken,
  initializeContextTokenOwnershipResponder,
  isContextTokenOwnedByAnotherTab,
} from "src/utils/contextToken"

const CLAIM_THROTTLE_MS = 5000

export type ClaimSessionFn = () => Promise<{ kind: "ok" } | { kind: "error"; message: string }>

/**
 * Cold-bootstrap fallback: when the app or browser tab becomes active AND no
 * context token is present in storage, run the bootstrap (POST /context/switch isLogin:true).
 * With a token already present, the tab IS the active session — re-claiming
 * would mint a new session_id, invalidate the existing one, close the
 * WebSocket with 4001, and force a redundant silent recovery.
 *
 * The hook is essentially a no-op for normal page refreshes / tab focus
 * changes once a tab has been bootstrapped: sessionStorage (web) and MMKV
 * (native) both survive those events. It only fires when:
 *   - The user has just logged in but the bootstrap path missed (rare).
 *   - sessionStorage was cleared independently (web edge case).
 *   - An explicit recovery cleared the token.
 *
 * Race guard: when `isBootstrappingSession` is true, a higher-level flow
 * (signIn or sponsor switch) is already establishing the session. The mount
 * and foreground claims are suppressed in that case — firing a parallel
 * bootstrapping would mint a competing session_id and deactivate the session
 * the bootstrap flow just established, surfacing spurious 401
 * SESSION_DEACTIVATED responses on requests that fired during the window.
 */
export function useClaimSessionOnForeground(options: {
  initialAuthStateLoaded: boolean
  isAuthenticated: boolean
  isSessionDeactivated: boolean
  isBootstrappingSession?: boolean
  claimSession: ClaimSessionFn
}) {
  const {
    initialAuthStateLoaded,
    isAuthenticated,
    isSessionDeactivated,
    isBootstrappingSession = false,
    claimSession,
  } = options

  const lastClaimAtRef = useRef(0)
  const appState = useAppState()
  const prevAppStateRef = useRef(appState)
  // Tracks whether we have already fired (or intentionally skipped) the
  // one-shot mount claim so we don't repeat it if the dependencies re-render.
  const hasMountClaimedRef = useRef(false)
  // Tracks whether we have already done the one-time cross-tab ownership
  // check at mount. Subsequent foreground events do not re-check (the
  // sessionStorage inheritance race is a mount-only concern).
  const hasCheckedTokenOwnershipRef = useRef(false)

  // Cross-tab ownership: register the responder once at mount so this tab
  // can answer "do you own this context token?" queries from other tabs
  // (e.g. a freshly Cmd+clicked tab that inherited our sessionStorage).
  // No-op on native / browsers without BroadcastChannel.
  useEffect(() => {
    initializeContextTokenOwnershipResponder()
  }, [])

  const maybeClaim = useCallback(async () => {
    if (!initialAuthStateLoaded || !isAuthenticated || isSessionDeactivated) {
      return
    }
    if (isBootstrappingSession) {
      console.log("[useClaimSessionOnForeground] Skipping claim — bootstrap in progress")
      return
    }
    // A stored context token IS the active-session marker. Calling
    // bootstrap (POST /context/switch) again would mint a competing session_id
    // and invalidate the existing one. Skip if a token is already present.
    const stored = getContextToken()
    if (stored) {
      // Cross-tab inheritance check (web-only; native is a no-op). On
      // certain browsers a `target=_blank` / Cmd+click new tab
      // inherits the opener's sessionStorage. Both tabs would then think
      // they own the same context_token; the moment one rotates it the
      // other gets WS 4001 / 401 SESSION_DEACTIVATED. Before trusting the
      // inherited token, ask "anyone else own this?" — if yes, clear our
      // local copy and fall through to bootstrap a fresh session for this
      // tab. Only run once per tab lifecycle (the inheritance race is a
      // mount-only concern).
      if (!hasCheckedTokenOwnershipRef.current) {
        hasCheckedTokenOwnershipRef.current = true
        const ownedByAnother = await isContextTokenOwnedByAnotherTab(stored)
        if (ownedByAnother) {
          console.log(
            "[useClaimSessionOnForeground] Stored context token is owned by another tab — clearing and bootstrapping fresh",
          )
          clearContextToken()
          // Fall through to claimSession below now that the inherited
          // token has been discarded.
        } else {
          return
        }
      } else {
        return
      }
    }
    const now = Date.now()
    if (now - lastClaimAtRef.current < CLAIM_THROTTLE_MS) {
      return
    }
    lastClaimAtRef.current = now
    console.log("[useClaimSessionOnForeground] Claiming session (cold bootstrap)")
    await claimSession()
  }, [
    initialAuthStateLoaded,
    isAuthenticated,
    isSessionDeactivated,
    isBootstrappingSession,
    claimSession,
  ])

  // On cold launch the app starts already in "active" state, so the AppState
  // transition effect below never fires. This effect handles that case: once
  // auth state is ready and the user is authenticated, claim exactly once.
  useEffect(() => {
    if (
      !hasMountClaimedRef.current &&
      initialAuthStateLoaded &&
      isAuthenticated &&
      !isSessionDeactivated
    ) {
      // If a bootstrap is already running (signIn or sponsor switch) on the
      // very render that flips isAuthenticated, mark the mount claim as
      // already handled and skip. Critically, we set hasMountClaimedRef
      // unconditionally — that prevents this effect from re-firing after
      // bootstrap completes and racing against in-flight signIn requests
      // (getRoleInfo, reportActivity, etc.).
      hasMountClaimedRef.current = true
      if (isBootstrappingSession) {
        console.log("[useClaimSessionOnForeground] Skipping mount claim — bootstrap in progress")
        return
      }
      console.log("[useClaimSessionOnForeground] Claiming session (mount / cold launch)")
      maybeClaim().catch(console.error)
    }
  }, [
    initialAuthStateLoaded,
    isAuthenticated,
    isSessionDeactivated,
    isBootstrappingSession,
    maybeClaim,
  ])

  useEffect(() => {
    const prev = prevAppStateRef.current
    prevAppStateRef.current = appState
    if (prev !== "active" && appState === "active") {
      maybeClaim().catch(console.error)
    }
  }, [appState, maybeClaim])

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return
    }
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        maybeClaim().catch(console.error)
      }
    }
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [maybeClaim])
}
